import React, { useState } from 'react';
import { prop, gt, equals, length, sum } from 'ramda';
import { PlusOutlined } from '@ant-design/icons';
import useUserRefreshLock from '../../hooks/useUserRefreshLock';
import { Title, Actions, LinkButton } from '../../components';
import withUser from '../../withUser';
import QGetAllFrames from '../../_graphql/queries/documents/QGetAllFrames';
import useCompare from '../../hooks/useCompare';
import ModalLoading from '../../components/ModalLoading';
import OperationStatusSection from '../../components/OperationStatusSection';
import { getOfferIdsByUserTeam } from '../helpers/getOfferIdsByUserTeam';
import FramesList from './FramesList';

const MyFrames = ({ userIsAdmin, userTeam, user }) => {
  const [refetch, setRefetch] = useState(false);
  const [selectedFrames, setSelectedFrames] = useState([]);
  const { lockUserRefresh, unlockUserRefresh } = useUserRefreshLock();
  const {
    loadingOnCompareAll,
    loadingOnCompareOnRow,
    loadingOnCompareOnSelected,
    onCompare,
    nbComparisons,
    progress,
    openModalVisible,
    setIsModalVisible,
    statusComparisons,
    elapsedTime
  } = useCompare({
    userId: prop('userId', user),
    frameIds: selectedFrames,
    lockUserRefresh
  });
  const areManyComparisons = nbComparisons > 1;
  const hasFailedComparison = gt(prop('failed', statusComparisons), 0);
  const hasErrorComparison = gt(prop('error', statusComparisons), 0);
  const totalComparisonsDone = sum([prop('passed', statusComparisons), prop('failed', statusComparisons), prop('error', statusComparisons)]);

  const errorCount = length(prop('error', statusComparisons));
  const ERROR_COMPARISON_MSG = `Impossible de générer ${errorCount > 1 ? 'plusieurs' : 'une'} comparaison${errorCount > 1 ? 's' : ''}, veuillez contacter l'équipe Flexidocs`;
  const MULTIPLE_DIFFERENCES_MSG = 'Les comparaisons ont révélé des différences. Vous pouvez les consulter en allant sur les informations de générations.';
  const SINGLE_DIFFERENCE_MSG = 'La comparaison a révélé des différences. Vous pouvez les consulter en allant sur les informations de générations.';
  const errorMsg = hasErrorComparison
    ? ERROR_COMPARISON_MSG
    : areManyComparisons
      ? MULTIPLE_DIFFERENCES_MSG
      : SINGLE_DIFFERENCE_MSG;
  const offerIds = getOfferIdsByUserTeam(userTeam, true);

  return (
    <>
      <ModalLoading
        visible={openModalVisible}
        onClick={() => {
          setIsModalVisible(false);
          setRefetch(true);
          unlockUserRefresh();
        }}
        progress={progress}
        elapsedTime={elapsedTime}
        infoLoading={(
          <OperationStatusSection
            operationType="vos comparaisons"
            items={[
              { label: 'Comparaisons sans différences', type: 'passed' },
              { label: 'Différences détectées', type: 'failed' },
              { label: 'Comparaisons échouées', type: 'error' },
              { label: <strong>Total comparaisons réalisées</strong>, type: 'total' }
            ]}
            status={{
              passed: prop('passed', statusComparisons),
              failed: prop('failed', statusComparisons),
              error: prop('error', statusComparisons),
              total: totalComparisonsDone
            }}
            progress={progress}
            errorMsg={errorMsg}
            showError={equals(100, progress) && (hasFailedComparison || hasErrorComparison)}
            displayValue
          />
        )}
        textProgression={`${totalComparisonsDone} sur ${nbComparisons}`}
      />
      <QGetAllFrames
        args={{
          teamId: prop('teamId', userTeam), offerIds, refetch, setRefetch
        }}
      >
        {({ allFrames }) => (
          <>
            <Actions
              alignButtonEndRight
              title={(
                <Title
                  title="mes trames"
                  icon="/images/icon-comparer@3x.png"
                  iconSize={[20, 22]}
                  iconOffset={-2}
                  filter="invert(100%)"
                />
              )}
              buttons={(
                <LinkButton
                  to="/trames/creer"
                  size="large"
                  icon={<PlusOutlined />}
                  disabled={loadingOnCompareOnRow || loadingOnCompareOnSelected}
                >
                  Créer une trame
                </LinkButton>
              )}
            />

            <FramesList
              userIsAdmin={userIsAdmin}
              onCompare={onCompare}
              loadingOnCompare={loadingOnCompareAll || loadingOnCompareOnSelected}
              loadingOnCompareOnRow={loadingOnCompareOnRow}
              frames={allFrames}
              setRefetch={setRefetch}
              setSelectedFrames={setSelectedFrames}
            />
          </>
        )}
      </QGetAllFrames>
    </>
  );
};

export default withUser(MyFrames);
